/**
 * Python 브리지 호출과 값 변환만 담당한다. 여기서 Serial 을 만지지 않는다.
 *
 * 임시로 만든 Python proxy 는 `finally` 에서 반드시 해제하고, 반복 호출용으로
 * 들고 있는 proxy(브리지 객체)는 세션을 버릴 때 해제한다. 문자열로 Python
 * 코드를 만들어 값을 넘기는 방법은 쓰지 않는다.
 */
import type { BridgeSnapshot, ProductInfo } from "../types";
import type { LoadedRuntime } from "./runtime";

export class BridgeValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BridgeValidationError";
  }
}

/** bool 을 숫자로 받지 않고 NaN·Infinity 를 거른다. */
function finiteNumber(value: unknown, what: string): number {
  if (typeof value === "boolean" || typeof value !== "number" || !Number.isFinite(value)) {
    throw new BridgeValidationError(`${what}: 유한한 숫자여야 합니다 (${String(value)})`);
  }
  return value;
}

function wholeNumber(value: unknown, what: string): number {
  const number = finiteNumber(value, what);
  if (!Number.isInteger(number)) {
    throw new BridgeValidationError(`${what}: 정수여야 합니다 (${String(value)})`);
  }
  return number;
}

export interface CreateOptions {
  maxSpeed: number;
  leftSign: number;
  rightSign: number;
}

export function readCatalog(runtime: LoadedRuntime): ProductInfo[] {
  const json = runtime.bridgeModule.catalog_json() as string;
  return JSON.parse(json) as ProductInfo[];
}

export class PythonBridge {
  private proxy: any;
  private disposed = false;
  readonly product: ProductInfo;

  private constructor(
    private readonly runtime: LoadedRuntime,
    proxy: any,
    product: ProductInfo,
  ) {
    this.proxy = proxy;
    this.product = product;
  }

  /** 연결을 시작할 때 한 번 만든다. tick 마다 만들지 않는다. */
  static create(
    runtime: LoadedRuntime,
    productId: string,
    options: CreateOptions,
  ): PythonBridge {
    const maxSpeed = wholeNumber(options.maxSpeed, "속도 상한");
    const leftSign = wholeNumber(options.leftSign, "왼쪽 모터 방향");
    const rightSign = wholeNumber(options.rightSign, "오른쪽 모터 방향");
    if (maxSpeed < 5 || maxSpeed > 100) {
      throw new BridgeValidationError(`속도 상한은 5~100% 입니다 (${maxSpeed})`);
    }
    if (![-1, 1].includes(leftSign) || ![-1, 1].includes(rightSign)) {
      throw new BridgeValidationError("모터 방향은 -1 또는 1 입니다");
    }

    const proxy = runtime.bridgeModule.create_bridge();
    try {
      const json = proxy.create(productId, maxSpeed, leftSign, rightSign) as string;
      return new PythonBridge(runtime, proxy, JSON.parse(json) as ProductInfo);
    } catch (error) {
      proxy.destroy();
      throw error;
    }
  }

  private alive(): any {
    if (this.disposed) {
      throw new BridgeValidationError("이미 폐기된 브리지입니다");
    }
    return this.proxy;
  }

  setInput(throttle: number, steering: number): void {
    const t = finiteNumber(throttle, "전후 입력");
    const s = finiteNumber(steering, "좌우 입력");
    if (t < -1 || t > 1 || s < -1 || s > 1) {
      throw new BridgeValidationError("주행 입력은 -1~1 입니다");
    }
    this.alive().set_input(t, s);
  }

  setSpeedLimit(percent: number): number {
    const value = wholeNumber(percent, "속도 상한");
    if (value < 5 || value > 100) {
      throw new BridgeValidationError(`속도 상한은 5~100% 입니다 (${value})`);
    }
    return this.alive().set_speed_limit(value) as number;
  }

  setOutput(port: number, value: number): number {
    const index = wholeNumber(port, "출력 포트");
    const amount = wholeNumber(value, "출력값");
    const spec = this.product.outputs[index - 1];
    if (!spec) {
      throw new BridgeValidationError(
        `출력 포트는 1~${this.product.out_ports} 입니다 (${index})`,
      );
    }
    if (amount < spec.min || amount > spec.max) {
      throw new BridgeValidationError(
        `${spec.label} 값은 ${spec.min}~${spec.max} 입니다 (${amount})`,
      );
    }
    return this.alive().set_output(index, amount) as number;
  }

  /** 보낼 차례에만 부른다 — AION 은 호출 시점에 시퀀스가 바뀐다. */
  makeTx(): Uint8Array {
    return this.toBytes(this.alive().make_tx());
  }

  makeStopTx(): Uint8Array {
    return this.toBytes(this.alive().make_stop_tx());
  }

  /** 받은 바이트를 파서에 넣고 이번에 나온 센서 프레임 수를 돌려준다. */
  feed(chunk: Uint8Array): number {
    const proxy = this.alive();
    const values = this.runtime.pyodide.toPy(Array.from(chunk));
    try {
      return proxy.feed(values) as number;
    } finally {
      values.destroy();
    }
  }

  snapshot(): BridgeSnapshot {
    return JSON.parse(this.alive().snapshot_json() as string) as BridgeSnapshot;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    try {
      this.proxy.dispose();
    } finally {
      this.proxy.destroy();
      this.proxy = null;
    }
  }

  private toBytes(result: any): Uint8Array {
    try {
      return Uint8Array.from(result.toJs() as number[]);
    } finally {
      result.destroy();
    }
  }
}
